import {v2, v3} from "./short3";
import PixelAccessor from "oc-canvas2d/PixelAccessor";
import Images from "oc-canvas2d/Images";

export default class MaterialConverter {

    convertAllPhongToStandard(object=STAGE.scene) {
        const materials = [];
        const replace = (m) => {
            if (!(m instanceof THREE.MeshPhongMaterial)) {
                return m;
            }
            const material = this.convertPhongToStandard(m);
            materials.push(material);
            return material;
        };
        object.traverse(o=>{
            if (o.material) {
                if (_.isArray(o.material)) {
                    o.material = o.material.map(replace);
                    o.material.forEach(m=>m.needsUpdate=true);
                } else {
                    o.material = replace(o.material);
                    o.material.needsUpdate = true;
                }
            }
        });
        return materials;
    }

    /**
     * @param {THREE.MeshPhongMaterial} pm
     * @returns {THREE.MeshStandardMaterial}
     */
    convertPhongToStandard(pm) {

        if (pm.__asMeshStandardMaterial) {
            return pm.__asMeshStandardMaterial;
        }

        const identicalProps = [
            'alphaMap','aoMap','aoMapIntensity',
            'color','map',
            'displacementMap','displacementScale','displacementBias',
            'emissive','emissiveScale','emissiveMap','emissiveIntensity',
            'envMap','envMapIntensity',
            'lightMap','lightMapIntensity',
            'refractionRatio',
            'skinning',
            'wireframe','wireframeLinecap','wireframeLinejoin','wireframeLinewidth'
        ];

        const m = {};

        identicalProps.filter(key=>pm[key]!=null).forEach(key=>{
            const v = pm[key];
            if (v.clone) {
                m[key] = v.clone();
                if (v.isTexture) {
                    m[key].needsUpdate = true;
                }
            } else {
                m[key] = v;
            }
        });

        // only phong: reflectivity, shininess, specular, specularMap,
        // only standard: roughness, roughnessMap, metalness, metalnessMap,
        // spezial: bumpMap, bumpScale => normal wegen GLTF kann nur normal

        m.roughness = this.convertSpecularTraditionalToRoughness(pm.specular.toArray(), pm.shininess);
        if (pm.specularMap!=null) {
            m.roughnessMap = this.convertSpecularTraditionalToRoughnessMap(pm.specularMap, pm.shininess);
        }
        m.metalness = 0;

        if (pm.bumpMap!=null) {
            m.normalMap = this.convertBumpMapToNormalMap(pm.bumpMap);
            m.normalMap.needsUpdate = true;
            const s = THREE.MathUtils.clamp(pm.bumpScale*0.3*100,-1,1);
            m.normalScale = v2(s, s);
            // m.normalMapType = THREE.ObjectSpaceNormalMap;
        }

        const sm = new THREE.MeshStandardMaterial(m);
        pm.__asMeshStandardMaterial = sm;
        sm.__asMeshPhongMaterial = pm;

        sm.name = pm.name;

        console.log('convertPhongToStandard',pm,m,sm);

        return sm;
    }

    convertSpecularTraditionalToRoughnessMap(specularMap, shininess) {
        const canvas = Images.canvasFromImage(specularMap.image);
        const pixels = new PixelAccessor(canvas);
        const rgba = pixels.createRGBA();
        pixels.forRaster((x,y)=>{
            pixels.getRGBA(x,y,rgba);
            const roughness = this.convertSpecularTraditionalToRoughness(rgba, shininess);
            rgba[0] = 0;
            rgba[1] = Math.round(roughness * 255);
            rgba[2] = 0;
            rgba[3] = 255;
            pixels.setRGBA(x,y,rgba);
        });
        pixels.done();
        return this.copyTexture(specularMap,Images.convertCanvasToImage(canvas));
    }

    convertBumpMapToNormalMap(bumpMap) {
        const normalCanvas = this.convertBumpMapImageToNormalImage(bumpMap.image);
        return this.copyTexture(bumpMap,normalCanvas);
    }

    convertBumpMapImageToNormalImage(bumpMapImage,invertR=false,invertG=false,bias=50) {
        // from http://www.smart-page.net/smartnormal/

        const bumpCanvas = Images.canvasFromImage(bumpMapImage);
        const bumpPixels = new PixelAccessor(bumpCanvas);

        const normalCanvas = Images.canvas(bumpMapImage.width,bumpMapImage.height);
        const normalPixels = new PixelAccessor(normalCanvas);

        const rgba = normalPixels.createRGBA();
        const n = v3();

        const dxSign = invertR ? -1 : 1;
        const dySign = invertG ? 1 : -1;

        normalPixels.forRaster((x,y)=>{

            const d0 = bumpPixels.getGray(x+0,y+0) / 255;
            const d1 = bumpPixels.getGray(x+1,y+0) / 255;
            const d2 = bumpPixels.getGray(x-1,y+0) / 255;
            const d3 = bumpPixels.getGray(x+0,y+1) / 255;
            const d4 = bumpPixels.getGray(x+0,y-1) / 255;

            const dx = ((d2-d0) + (d0-d1))*0.5;
            const dy = ((d4-d0) + (d0-d3))*0.5;

            n.x = dx * dxSign;
            n.y = dy * dySign;
            n.z = 1.0 - ((bias - 0.1) / 100.0);

            n.normalize();

            rgba[0] = Math.round((n.x * 0.5 + 0.5) * 255);
            rgba[1] = Math.round((n.y * 0.5 + 0.5) * 255);
            rgba[2] = Math.round((n.z * 0.5 + 0.5) * 255);
            rgba[3] = 255;

            normalPixels.setRGBA(x,y,rgba);
        });
        normalPixels.done();

        return Images.convertCanvasToImage(normalCanvas);
    }

    copyTexture(map,newImage) {
        const texture = new THREE.Texture(newImage,
            map.mapping,
            map.wrapS, map.wrapT,
            map.magFilter, map.minFilter,
            map.format,
            map.type, map.anisotropy, map.encoding);
        texture.flipY = map.flipY;
        texture.repeat.copy(map.repeat);
        texture.name = map.name+'-normal';
        return texture;
    }

    convertSpecularTraditionalToRoughness(specularColor, shininess) {
        // Translate the blinn-phong model to the pbr metallic-roughness model
        // Roughness factor is a combination of specular intensity and shininess
        // Metallic factor is 0.0
        // Textures are not converted for now
        const specularIntensity = this.luminance(specularColor);

        let roughness = shininess;
        roughness = roughness / 1000.0;
        roughness = 1.0 - roughness;
        roughness = THREE.MathUtils.clamp(roughness, 0.0, 1.0);

        // Low specular intensity values should produce a rough material even if shininess is high.
        if (specularIntensity < 0.1) {
            roughness *= (1.0 - specularIntensity);
        }
        return roughness;
    }

    luminance(color) {
        return color[0] * 0.2125 + color[1] * 0.7154 + color[2] * 0.0721;
    }


    // dielectricSpecular = new THREE.Color(0.04, 0.04, 0.04);
    // epsilon = 1e-6;
    //
    // solveMetallic(diffuse, specular, oneMinusSpecularStrength) {
    //
    //     if (specular < dielectricSpecular.r) {
    //         return 0;
    //     }
    //
    //     let a = dielectricSpecular.r;
    //     let b = diffuse * oneMinusSpecularStrength / (1 - dielectricSpecular.r) + specular - 2 * dielectricSpecular.r;
    //     let c = dielectricSpecular.r - specular;
    //     let D = Math.max(b * b - 4 * a * c, 0);
    //     return THREE.MathUtils.clamp((-b + Math.sqrt(D)) / (2 * a), 0, 1);
    //
    // }
    //
    // convertSpecularGlossinessPbrToMetallicRoughnessPbr(specularGlossiness) {
    //
    //     let diffuse = specularGlossiness.diffuse;
    //     let opacity = specularGlossiness.opacity;
    //     let specular = specularGlossiness.specular;
    //     let glossiness = specularGlossiness.glossiness;
    //
    //     let oneMinusSpecularStrength = 1 - specular.getMaxComponent();
    //     let metallic = this.solveMetallic(diffuse.getPerceivedBrightness(), specular.getPerceivedBrightness(), oneMinusSpecularStrength);
    //
    //     let baseColorFromDiffuse = diffuse.clone().multiplyScalar(oneMinusSpecularStrength / (1 - dielectricSpecular.r) / Math.max(1 - metallic, epsilon));
    //     let baseColorFromSpecular = specular.clone().sub(dielectricSpecular.clone().multiplyScalar(1 - metallic)).multiplyScalar(1 / Math.max(metallic, epsilon));
    //     let baseColor = baseColorFromDiffuse.clone().lerp(baseColorFromSpecular, metallic * metallic).clamp();
    //
    //     return {
    //         baseColor: baseColor,
    //         opacity: opacity,
    //         metallic: metallic,
    //         roughness: 1 - glossiness
    //     };
    // }




//     // All values are in linear space
//     public struct MetallicRoughness
// {
//     public Color BaseColor;
//     public float Metallic;
//     public float Roughness;
// }
//
// // All values are in linear space
// public struct SpecularGlossiness
// {
//     public Color Diffuse;
//     public Color Specular;
//     public float Glossiness;
// }
//
//
// public static MetallicRoughness Convert(SpecularGlossiness specularGlossiness)
// {
//     let diffuse = specularGlossiness.Diffuse;
//     let specular = specularGlossiness.Specular;
//     let glossiness = specularGlossiness.Glossiness;
//
//     let oneMinusSpecularStrength = 1.0f - specular.maxColorComponent;
//     let metallic = SolveMetallic(DielectricSpecular.r, GetPerceivedBrightness(diffuse), GetPerceivedBrightness(specular), oneMinusSpecularStrength);
//
//     let baseColorFromDiffuse = diffuse * oneMinusSpecularStrength / ((1.0f - DielectricSpecular.r) * Mathf.Max(1.0f - metallic, Epsilon));
//     let baseColorFromSpecular = (specular - DielectricSpecular * (1.0f - metallic)) / Mathf.Max(metallic, Epsilon);
//     let baseColor = Color.Lerp(baseColorFromDiffuse, baseColorFromSpecular, metallic * metallic);
//     baseColor.a = diffuse.a;
//
//     return new MetallicRoughness
// {
//     BaseColor = baseColor,
//     Metallic = metallic,
//     Roughness = 1.0f - glossiness,
// };
// }
//
// private static float GetPerceivedBrightness(Color linearColor)
// {
//     let r = linearColor.r;
//     let b = linearColor.b;
//     let g = linearColor.g;
//     return Mathf.Sqrt(0.299f * r * r + 0.587f * g * g + 0.114f * b * b);
// }
//
// private static float SolveMetallic(float dielectricSpecular, float diffuse, float specular, float oneMinusSpecularStrength)
// {
//     if (specular < dielectricSpecular)
//     {
//         return 0.0f;
//     }
//
//     let a = dielectricSpecular;
//     let b = diffuse * oneMinusSpecularStrength / (1 - dielectricSpecular) + specular - 2.0f * dielectricSpecular;
//     let c = dielectricSpecular - specular;
//     let D = b * b - 4.0f * a * c;
//     return Mathf.Clamp01((-b + Mathf.Sqrt(D)) / (2.0f * a));
// }
//
//     static convertBumpToNormalPixel(normal, idx, value, map) {
//         let x = idx[0]
//         let y = idx[1]
//
//         if (this.wrap) {
//             let sx = map.get(x < this.wlimit ? x + 1 : 0, y)
//                 - map.get(x > 0 ? x - 1 : this.wlimit, y)
//
//             let sy = map.get(x, y < this.hlimit ? y + 1 : 0)
//                 - map.get(x, y > 0 ? y - 1 : this.hlimit)
//         } else {
//             let sx = map.get(x < this.wlimit ? x + 1 : x, y)
//                 - map.get(x > 0 ? x - 1 : x, y)
//
//             let sy = map.get(x, y < this.hlimit ? y + 1 : y)
//                 - map.get(x, y > 1 ? y - 1 : y)
//
//             if (!x || x === this.wlimit) sx *= 2
//             if (!y || y === this.hlimit) sy *= 2
//         }
//
//         let mag = sx*sx+4+sy*sy
//         if (mag === 0) {
//             normal.set(x, y, 0, 0)
//             normal.set(x, y, 1, 0)
//             normal.set(x, y, 2, 0)
//         } else {
//             mag = Math.sqrt(mag)
//             normal.set(x, y, 0, (-sx / mag) * this.yscale)
//             normal.set(x, y, 2, (  2 / mag) * this.xzscale)
//             normal.set(x, y, 1, ( sy / mag) * this.yscale)
//         }
//     }
// }
//
// private Texture2D NormalMap(Texture2D source,float strength)
// {
//     strength=Mathf.Clamp(strength,0.0F,1.0F);
//
//     Texture2D normalTexture;
//     float xLeft;
//     float xRight;
//     float yUp;
//     float yDown;
//     float yDelta;
//     float xDelta;
//
//     normalTexture = new Texture2D (source.width, source.height, TextureFormat.ARGB32, true);
//
//     for (int y=0; y<normalTexture.height; y++)
//     {
//         for (int x=0; x<normalTexture.width; x++)
//         {
//             xLeft = source.GetPixel(x-1,y).grayscale*strength;
//             xRight = source.GetPixel(x+1,y).grayscale*strength;
//             yUp = source.GetPixel(x,y-1).grayscale*strength;
//             yDown = source.GetPixel(x,y+1).grayscale*strength;
//             xDelta = ((xLeft-xRight)+1)*0.5f;
//             yDelta = ((yUp-yDown)+1)*0.5f;
//             normalTexture.SetPixel(x,y,new Color(xDelta,yDelta,1.0f,yDelta));
//         }
//     }
//     normalTexture.Apply();
//
//     //Code for exporting the image to assets folder
//     System.IO.File.WriteAllBytes( "Assets/NormalMap.png", normalTexture.EncodeToPNG());
//
//     return normalTexture;
// }

}


window.MaterialConverter = MaterialConverter;
